import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

export default function WaveParticles({ count = 2000 }) {
  const particlesRef = useRef();

  const particles = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const sizes = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      // Random positions in a sphere
      const radius = Math.random() * 15 + 5;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;

      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = radius * Math.cos(phi) - 3;
      positions[i * 3 + 2] = radius * Math.sin(phi) * Math.sin(theta) - 5;

      // Ocean colors
      const color = new THREE.Color();
      color.setHSL(0.55 + Math.random() * 0.1, 0.7, 0.5 + Math.random() * 0.3);
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;

      sizes[i] = Math.random() * 0.05 + 0.01;
    }

    return { positions, colors, sizes };
  }, [count]);

  useFrame(({ clock }) => {
    if (!particlesRef.current) return;

    const time = clock.getElapsedTime();
    const positions = particlesRef.current.geometry.attributes.position.array;

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      const x = positions[i3];
      const y = positions[i3 + 1];
      const z = positions[i3 + 2];

      // Enhanced flowing wave motion with multiple wave frequencies
      const wave1 = Math.sin(time * 0.5 + x * 0.2) * 0.003;
      const wave2 = Math.cos(time * 0.3 + z * 0.3) * 0.002;
      const wave3 = Math.sin(time * 0.7 + (x + z) * 0.15) * 0.0015;

      positions[i3 + 1] = y + wave1 + wave2 + wave3;

      // Smooth rotation with pulsing effect
      const angle = time * 0.08;
      const pulse = Math.sin(time * 0.2) * 0.3 + 1;
      positions[i3] = (x * Math.cos(angle * 0.1) - z * Math.sin(angle * 0.1)) * pulse;
      positions[i3 + 2] = (x * Math.sin(angle * 0.1) + z * Math.cos(angle * 0.1)) * pulse;
    }

    particlesRef.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <points ref={particlesRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={particles.positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={count}
          array={particles.colors}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-size"
          count={count}
          array={particles.sizes}
          itemSize={1}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.08}
        vertexColors
        transparent
        opacity={0.7}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
}
